import { Component, OnInit, Inject } from '@angular/core';
import { FormGroup, FormControl, Validators, FormArray, FormBuilder } from '@angular/forms';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { ToastrService } from 'ngx-toastr';
import { InfiniteResult } from 'src/app/_models/body';
import { UploadFileDto } from 'src/app/_models/uploadFileDto';
import { FileUploadService } from 'src/app/_services/file-upload.service';
import { Global } from 'src/app/_services/global.service';
import { StreamService } from 'src/app/_services/streamer.service';

@Component({
  selector: 'app-streaming-item',
  templateUrl: './streaming-item.component.html',
  styleUrls: ['./streaming-item.component.scss']
})
export class StreamingItemComponent implements OnInit {
  formGroup: FormGroup;
  isEditMode: boolean = false;

  // Options for select fields
  readonly assetOptions: any[] = [];
  readonly streamerOptions: any[] = [];
  readonly stageOptions: any[] = [];
  readonly channelStatusOptions: any[] = this.global.section.StreamChannelStatusSelections;
  readonly eventStatusOptions: any[] = this.global.section.StreamEventStatusSelections;
  readonly typeOptions: any[] = this.global.section.StreamEventTypeSelections;
  readonly recordStatusOptions: any[] = this.global.section.ChannelRecordStatusSelections;
  readonly streamChannelRecordFileVersionSelections: any[] = this.global.section.StreamChannelRecordFileVersionSelections;
  readonly streamChannelBroadcasterStatusSelections: any[] = this.global.section.StreamChannelBroadcasterStatusSelections;
  readonly StreamEventModeSelections = this.global.section.StreamEventModeSelections
  orgData: any // 传递初始数据
  updatedFlag: boolean

  constructor(
    private fb: FormBuilder,
    private modalRef: NzModalRef,
    private fileUploadService: FileUploadService,
    private global: Global,
    private streamService: StreamService,
    private toastr: ToastrService,
    private nzModalService: NzModalService
  ) { }

  // 在初始化时添加type的值变化监听
  ngOnInit(): void {
    this.isEditMode = !!this.orgData;
    this.initForm();

    // 监听type字段的变化
    this.formGroup.get('type')?.valueChanges.subscribe((type: string) => {
      this.handleTypeChange(type);
    });

    // 如果有初始数据，填充表单
    if (this.isEditMode) {
      this.patchFormData(this.orgData);
    } else {
      // 初始化时根据默认值设置channel状态
      this.handleTypeChange(this.formGroup.get('type')?.value);
    }
  }

  private handleTypeChange(type: string) {
    const channelControl = this.formGroup.get('channel');
    if (!channelControl) return;

    if (type === 'SimulateLiveStream') {
      channelControl.enable();
      const recordStatusControl = channelControl.get('recordStatus');
      recordStatusControl?.setValue('Success');
      recordStatusControl?.disable();
      return;
    }

    if (this.isEditMode) {
      channelControl.enable();
      return;
    }

    // RealtimeLiveStream 或其他类型
    channelControl.disable();
    channelControl.reset({
      status: '',
      recordStatus: '',
      recordFileVersion: '',
      startedAt: '',
      finishedAt: '',
      duration: 0,
      recordFiles: [],
    });
  }


  initForm() {
    this.formGroup = this.fb.group({
      // Basic information
      type: ['RealtimeLiveStream', Validators.required],
      description: ['', Validators.required],
      streamerId: ['', Validators.required],
      assetId: ['', Validators.required],
      theme: ['', Validators.required],
      stageIds: [[]],
      coverUrl: ['', Validators.required],
      startedAt: ['', Validators.required],
      mode: ['Default'],
      status: ['Enable'],

      // Channel information
      channel: this.fb.group({
        status: [''],
        recordStatus: [''],
        recordFileVersion: [''],
        startedAt: [''],
        finishedAt: [''],
        duration: [0],
        recordFiles: this.fb.array([]),
      }),

    });
  }


  patchFormData(orgData: any) {
    if (!orgData) return;

    // 填充基本字段
    this.formGroup.patchValue({
      type: orgData.type || 'RealtimeLiveStream',
      theme: orgData.theme || '',
      description: orgData.description || '',
      assetId: orgData.asset?.id || orgData.assetId || '',
      startedAt: orgData.startedAt || '',
      streamerId: orgData.streamer?.id || orgData.streamerId || '',
      stageIds: orgData.stages?.map((item: any) => item?.id) || orgData.stageIds || [],
      coverUrl: orgData.cover?.url || orgData.coverUrl || '',
      mode: orgData.mode || 'Default',
      status: orgData.status || 'Enable',
    });

    // 标记表单控件为未touched，避免显示验证错误
    Object.keys(this.formGroup.controls).forEach(key => {
      this.formGroup.get(key)?.markAsUntouched();
    });

    // 填充channel对象
    if (orgData.channel) {
      this.formGroup.get('channel')?.patchValue({
        status: orgData.channel.status || '',
        recordStatus: orgData.channel.recordStatus || '',
        recordFileVersion: orgData.channel.recordFileVersion || '',
        startedAt: orgData.channel.startedAt || '',
        finishedAt: orgData.channel.finishedAt || '',
        duration: orgData.channel.duration || 0,
      });

      // 标记channel控件为未touched
      const channelGroup = this.formGroup.get('channel');
      if (channelGroup instanceof FormGroup) {
        Object.keys(channelGroup.controls).forEach(key => {
          channelGroup.get(key)?.markAsUntouched();
        });
      }

      // 填充recordFiles数组
      if (orgData.channel.recordFiles && orgData.channel.recordFiles.length > 0) {
        this.recordFiles.clear();
        orgData.channel.recordFiles.forEach((file: any) => {
          this.addRecordFile(file.partialKey, file.fileUrl);
        });
      }
    }
  }

  // Record Files management
  get recordFiles(): FormArray {
    return this.formGroup.get('channel.recordFiles') as FormArray;
  }

  addRecordFile(partialKey: string = '', fileUrl: string = '') {
    this.recordFiles.push(this.fb.group({
      partialKey: [partialKey],
      fileUrl: [fileUrl]
    }));
  }

  removeRecordFile(index: number) {
    this.recordFiles.removeAt(index);
  }

  // Form submission - 简化版本
  async checkValidation() {
    console.log('checkValidation called, form valid:', this.formGroup.valid);
    console.log('Form errors:', this.formGroup.errors);
    if (this.formGroup.valid) {
      try {
        const finalData = await this.prepareSubmitDataWithUpload();
        this.onSubmit(finalData, this.orgData?.id)
      } catch (error) {
        console.error('File upload failed:', error);
        this.toastr.error('File upload failed', 'Error');
      }
    } else {
      console.log('Form invalid, marking as touched');
      this.markFormGroupTouched(this.formGroup);
      this.toastr.warning('Please fill in all required fields', 'Validation Error');
    }
  }

  onSubmit(formValue, id?) {
    id ? this.streamService.updateStreamingEvent(formValue, id).subscribe((res) => {
      this.modalRef.close(res);
    }) : this.streamService.createStreamingEvent(formValue).subscribe((res) => {
      this.modalRef.close(res);
    })
  }


  // 准备提交数据（包含上传逻辑）
  private async prepareSubmitDataWithUpload(): Promise<any> {
    const formValue = this.formGroup.value;
    const files: UploadFileDto[] = [];
    // 收集需要上传的文件
    if (formValue.coverUrl?.[0]?.file instanceof File) {
      files.push(new UploadFileDto({
        name: formValue.coverUrl[0].guid,
        file: formValue.coverUrl[0].file,
        guid: formValue.coverUrl[0].guid
      }));
    }

    formValue.channel?.recordFiles?.forEach((recordFile: any) => {
      if (recordFile.fileUrl?.[0]?.file instanceof File) {
        files.push(new UploadFileDto({
          name: recordFile.fileUrl[0].guid,
          file: recordFile.fileUrl[0].file,
          guid: recordFile.fileUrl[0].guid
        }));
      }
    });

    // 直接使用 formValue，不需要额外的 prepareSubmitData
    const finalData = { ...formValue };

    // 如果有文件需要上传
    if (files.length > 0) {
      const uploadResults = await this.fileUploadService.postFile(files).toPromise();

      // 更新URL
      if (finalData.coverUrl?.[0]) {
        const coverResult = uploadResults.find(res => res.name === finalData.coverUrl[0].guid);
        if (coverResult) {
          finalData.coverUrl = coverResult.origin;
        }
      }

      if (finalData.channel?.recordFiles) {
        finalData.channel.recordFiles.forEach((recordFile: any, index: number) => {
          if (recordFile.fileUrl?.[0]) {
            const fileResult = uploadResults.find(res => res.name === recordFile.fileUrl[0].guid);
            if (fileResult) {
              finalData.channel.recordFiles[index].fileUrl = fileResult.origin;
            }
          }
        });
      }
    }

    return finalData;
  }

  markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else if (control instanceof FormArray) {
        control.controls.forEach(arrayControl => {
          if (arrayControl instanceof FormGroup) {
            this.markFormGroupTouched(arrayControl);
          }
        });
      } else {
        control?.markAsTouched();
      }
    });
  }

  cancel() {
    this.modalRef.close();
  }

  calculateFinishTime() {
    const startedAt = this.formGroup.get('channel.startedAt')?.value;
    const duration = this.formGroup.get('channel.duration')?.value;

    if (!startedAt || !duration) return;

    const finishTime = new Date(new Date(startedAt).getTime() + (duration * 1000));
    this.formGroup.get('channel.finishedAt')?.setValue(finishTime.toISOString());

  }

  // 失去焦点时验证并修正为整数
  validateInteger() {
    const control = this.formGroup.get('channel.duration');
    if (control.value !== null && control.value !== undefined) {
      // 转换为正整数（取绝对值后取整）
      const intValue = Math.floor(Math.abs(control.value));
      control.setValue(intValue);
    }
  }

  setModeNow() {
    if (!this.isEditMode) {
      return
    }
    this.streamService.getStreamingEvents({ Id: this.orgData?.id }).subscribe((res: InfiniteResult) => {
      const data = res.data[0]
      const body = {
        ...this.mapToPutBody(data),
        mode: this.formGroup.get('mode')?.value // 如果你想覆盖 mode
      };

      this.streamService.updateStreamingEvent(body, this.orgData.id).subscribe((res: any) => {
        this.toastr.success(`set to ${res.mode} Mode`)
        this.reloadFormData()
      })
    })

  }

  reloadFormData() {
    this.streamService.getStreamingEvents({ Id: this.orgData.id }).subscribe(res => {
      this.orgData = res.data[0]
      this.patchFormData(this.orgData)
      this.updatedFlag = true
    })
  }

  playNow() {
    this.streamService.getStreamingEvents({ Id: this.orgData?.id }).subscribe((res: InfiniteResult) => {
      const data = res.data[0]
      this.nzModalService.confirm({
        nzTitle: `Current Channel Status: ${data?.channel?.status}`,
        nzContent: `${data?.channel?.status === 'Streaming' ? `Resetting the start time may affect the live broadcast being played. Are you sure you want to set the start time to now?` : `Are you sure you want to set the start time to now?`}`,
        nzOkText: 'Confirm',
        nzCancelText: 'Cancel',
        nzOnOk: () => {
          const finishTime = new Date(new Date().getTime() + (data.channel.duration * 1000));

          const body = {
            ...this.mapToPutBody(data),
            channel: {
              ...data.channel,
              status: 'Streaming',
              startedAt: new Date().toISOString(),
              finishedAt: finishTime.toISOString()
            }
          };

          this.streamService.updateStreamingEvent(body, data.id).subscribe((res: any) => {
            this.toastr.success(`Set 【${res.description}】 start time at ${res.channel.startedAt}`)
            this.reloadFormData()
          })
        },
      });
    })
  }


  mapToPutBody(orgData: any): any {
    return {
      type: orgData.type,
      description: orgData.description,
      theme: orgData.theme,
      streamerId: orgData.streamer?.id,
      assetId: orgData.asset?.id,
      stageIds: orgData.stages?.map((s: any) => s.id) || [],
      startedAt: orgData.channel?.startedAt || orgData.startedAt,
      status: orgData.status,
      mode: orgData.mode,
      coverUrl: orgData.cover?.url || orgData.asset?.thumbnail?.url || '',
      channel: {
        status: orgData.channel?.status,
        recordStatus: orgData.channel?.recordStatus,
        recordFileVersion: orgData.channel?.recordFileVersion,
        recordFiles: orgData.channel?.recordFiles || [],
        startedAt: orgData.channel?.startedAt,
        finishedAt: orgData.channel?.finishedAt,
        duration: orgData.channel?.duration
      },
    };
  }

}